import moment from 'moment';
import React, { useState } from 'react';
import styled from 'styled-components';
import FontAwesome from '~/javascripts/admin/components/atoms/FontAwesome';
import { openAlertModal } from '~/javascripts/admin/actions/alert-modal';
import { getClearing, getClearingAutomationSetting } from '~/javascripts/admin/actions/clearing';
import { showOrganization } from '~/javascripts/admin/actions/organization';
import { useAppDispatch } from '~/javascripts/admin/hooks/useApp';
import { downloadPaymentNoticePdf } from '~/javascripts/admin/components/shared/PaymentNotice';
import {
  PaymentNoticeData,
  bankAccountTypeLabel,
  computeScheduledPaymentDate,
  merchantRegistrationNumber,
} from '~/javascripts/admin/utils/paymentNotice';

// ClearingShowResponse.detail はスキーマ上 opaque(Record<string, never>)なので、
// 取り出すフィールドだけローカルに型付けして cast する。
type ClearingDetailFields = {
  transfer_invoice_amount?: number;
  transfer_payment_amount?: number;
  system_fee_count?: number;
  fixed_fee_count?: number;
};

type Props = {
  clearingId: string;
  privateMoneyId: string;
  small?: boolean;
  // 指定するとアイコンの右にラベルを併記する(詳細画面用。一覧はアイコンのみ)。
  label?: string;
  className?: string;
};

// ファイル名に使えない文字(パス区切り・制御文字等)を '_' に置換する。
const sanitizeFileNamePart = (s: string): string =>
  s.replace(/[\\/:*?"<>|]/g, '_').trim();

// スペース節約のためアイコンのみのボタン。ラベルは title/aria-label で補う。
const NoticeIconButton = styled.button<{ $small?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: #2f6fb0;
  cursor: pointer;
  line-height: 1;
  border-radius: 4px;
  padding: ${({ $small }) => ($small ? '2px 6px' : '4px 8px')};
  font-size: ${({ $small }) => ($small ? '14px' : '16px')};

  &:hover {
    background: #f0f6fc;
    color: #1f4e79;
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

// 支払通知書(PDF)ダウンロードボタン。一覧の各行・詳細画面の双方から使う共有部品。
// クリック時に clearing 詳細・加盟店組織・自動精算設定を取得し、フロントで PDF を生成する。
const PaymentNoticeDownloadButton = ({
  clearingId,
  privateMoneyId,
  small,
  label,
  className,
}: Props) => {
  const dispatch = useAppDispatch();
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const resp = await getClearing(clearingId);
      const clearing = resp.clearing;
      if (!clearing) throw new Error('clearing not found');
      const detail = (resp.detail ?? {}) as unknown as ClearingDetailFields;
      const code = clearing.claimed_organization?.code ?? '';

      // 相殺の内訳。支払側の offset_amount は各 offset(予定込み)の合算なので、
      // 通知書では充当先の請求データ(相手側)の期間ごとに1行で明示する。
      const offsets = (resp.offsets ?? []).map((o) => {
        const cp = o.counterpart_clearing;
        return {
          periodFrom: cp?.from ? moment(cp.from).format('YYYY/MM/DD') : '',
          periodTo: cp?.to ? moment(cp.to).format('YYYY/MM/DD') : '',
          amount: o.amount ?? 0,
        };
      });

      // 振込先口座・法人番号は加盟店組織から、お支払予定日は自動精算設定から取得する。
      // いずれも取得失敗しても通知書自体は出せるよう、該当項目を空にして続行する。
      const [org, setting] = await Promise.all([
        code ? showOrganization(code).catch(() => undefined) : Promise.resolve(undefined),
        getClearingAutomationSetting(privateMoneyId).catch(() => null),
      ]);

      const scheduled = computeScheduledPaymentDate(
        clearing.to,
        setting?.monthly_settlement_dates
      );
      // お支払金額・合計は実振込額(相殺後)を正とする。相殺なしの古いデータ等で null の場合は
      // 精算 net(payment − invoice)へフォールバックする。
      const paymentAmount =
        clearing.actual_transfer_amount ??
        (clearing.payment_amount ?? 0) - (clearing.invoice_amount ?? 0);
      const systemFee = clearing.system_fee ?? 0;
      const fixedFee = clearing.fixed_fee ?? 0;
      // 発行日は支払データ(精算データ)の作成日。API が created_at を返すまでは当日にフォールバック。
      // (created_at はスキーマ未定義のため cast で参照)
      const createdAt = (clearing as { created_at?: string | null }).created_at;

      // 電子マネー名・発行体コードはスキーマ未反映のため cast で参照。
      const privateMoneyName =
        (clearing as { private_money_name?: string }).private_money_name ?? '';

      const data: PaymentNoticeData = {
        id: clearing.id ?? '',
        privateMoneyName,
        issueDate: createdAt
          ? moment(createdAt).format('YYYY/MM/DD')
          : moment().format('YYYY/MM/DD'),
        issuerName: clearing.issuer_organization_name ?? '',
        issuerRegistrationNumber: clearing.issuer_invoice_registration_number ?? '',
        periodFrom: clearing.from ? moment(clearing.from).format('YYYY/MM/DD') : '',
        periodTo: clearing.to ? moment(clearing.to).format('YYYY/MM/DD') : '',
        merchantName: clearing.claimed_organization?.name ?? '',
        merchantRegistrationNumber: merchantRegistrationNumber(org?.corporate_number),
        paymentAmount,
        scheduledPaymentDate: scheduled ? scheduled.format('YYYY/MM/DD') : '',
        bankName: org?.bank_name ?? '',
        bankBranchName: org?.bank_branch_name ?? '',
        bankAccountTypeLabel: bankAccountTypeLabel(org?.bank_account_type),
        bankAccount: org?.bank_account ?? '',
        bankAccountHolderName: org?.bank_account_holder_name ?? '',
        transactionInvoiceAmount: detail.transfer_invoice_amount ?? 0,
        transactionPaymentAmount: detail.transfer_payment_amount ?? 0,
        offsetAmount: clearing.offset_amount ?? 0,
        offsets,
        // 課税対象料金。system は月数(detail.system_fee_count)倍、fixed は計上有無(0/1)倍。
        // count 欠落時のフォールバックは ClearingDetail 画面の既定値に揃える
        // (system は 0 件扱い / fixed は料金0なら0件・それ以外1件)。
        systemFeeAmount: systemFee * (detail.system_fee_count ?? 0),
        fixedFeeAmount: fixedFee * (detail.fixed_fee_count ?? (fixedFee === 0 ? 0 : 1)),
        consumptionTaxAmount: clearing.consumption_tax_amount ?? 0,
      };

      // ファイル名: 支払通知書_{マネー名}_{加盟店コード}_{締日YYYYMMDD}.pdf
      const closingDatePart = clearing.to
        ? moment(clearing.to).format('YYYYMMDD')
        : moment().format('YYYYMMDD');
      const moneyPart = sanitizeFileNamePart(privateMoneyName) || 'money';
      const claimedCode = sanitizeFileNamePart(
        clearing.claimed_organization?.code ?? clearing.id ?? 'unknown'
      );
      const fileName = `支払通知書_${moneyPart}_${claimedCode}_${closingDatePart}.pdf`;

      await downloadPaymentNoticePdf(data, fileName);
    } catch (e) {
      dispatch(
        openAlertModal({
          body: '支払通知書の作成に失敗しました。時間をおいて再度お試しください。',
          useCancel: false,
        })
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <NoticeIconButton
      type="button"
      $small={small}
      className={className}
      onClick={handleClick}
      disabled={busy}
      aria-label="支払通知書をダウンロード"
      title="支払通知書をダウンロード"
      data-html2canvas-ignore
    >
      <FontAwesome icon={busy ? 'sync' : 'download'} spin={busy} />
      {label ? <span style={{ marginLeft: 6 }}>{label}</span> : null}
    </NoticeIconButton>
  );
};

export default PaymentNoticeDownloadButton;
